import { useEffect, useState, type FormEvent } from 'react';
import { pushAc, pushDurumu, pushKapat, type PushDurum } from '../lib/push';
import { api, ApiHata } from '../lib/api';
import { useOturum } from '../lib/oturum';

/**
 * HESABIM — sifre degistirme.
 *
 * Sifre degisince TUM OTURUMLAR kapanir (sunucu tarafi) — calinan bir cihazdaki
 * oturumun sifre degistirilerek dusurulebilmesi icin. Kullanici yeniden girer.
 */
export function Hesabim() {
  const { ben, cikis } = useOturum();
  const [eski, setEski] = useState('');
  const [yeni, setYeni] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [bitti, setBitti] = useState(false);

  const zayif = yeni.length > 0 && yeni.length < 8;
  const uyusmuyor = tekrar.length > 0 && yeni !== tekrar;

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setMesgul(true);
    try {
      await api.post('/auth/parola-degistir', { eskiParola: eski, yeniParola: yeni });
      setBitti(true);
      // Sunucu tum oturumlari kapatti; yerel durumu da temizle.
      setTimeout(() => void cikis(), 2000);
    } catch (err) {
      setHata(
        err instanceof ApiHata ? err.message : 'Şifre değiştirilemedi. Tekrar deneyin.',
      );
      setMesgul(false);
    }
  }

  if (!ben) return null;

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2.5 text-sm outline-none focus:border-navy';

  return (
    <div className="mx-auto max-w-md p-4 lg:p-6">
      <h1 className="mb-1 text-xl font-semibold text-navy">Hesabım</h1>
      <p className="mb-5 text-sm text-wait">
        {ben.ad} · <span className="num">{ben.telefon}</span>
      </p>

      <EpostaAyari />

      <BildirimAyari />

      {bitti ? (
        <div className="rounded-(--radius-mod) border border-ok/30 bg-ok/5 p-5 text-center">
          <p className="font-medium text-ok">Şifre değiştirildi ✓</p>
          <p className="mt-1 text-sm text-wait">
            Güvenlik için tüm oturumlar kapatıldı. Yeni şifrenizle tekrar girin.
          </p>
        </div>
      ) : (
        <form
          onSubmit={gonder}
          className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-5"
        >
          <h2 className="mb-4 font-semibold text-navy">Şifre değiştir</h2>

          <label className="mb-1 block text-sm font-medium text-ink-2">Mevcut şifre</label>
          <input
            className={`${gir} mb-4`}
            type="password"
            autoComplete="current-password"
            value={eski}
            onChange={(e) => setEski(e.target.value)}
            required
          />

          <label className="mb-1 block text-sm font-medium text-ink-2">Yeni şifre</label>
          <input
            className={gir}
            type="password"
            autoComplete="new-password"
            value={yeni}
            onChange={(e) => setYeni(e.target.value)}
            required
          />
          <p className={`mb-4 mt-1 text-xs ${zayif ? 'text-crit' : 'text-wait'}`}>
            En az 8 karakter.
          </p>

          <label className="mb-1 block text-sm font-medium text-ink-2">Yeni şifre tekrar</label>
          <input
            className={gir}
            type="password"
            autoComplete="new-password"
            value={tekrar}
            onChange={(e) => setTekrar(e.target.value)}
            required
          />
          {uyusmuyor && <p className="mt-1 text-xs text-crit">Şifreler uyuşmuyor.</p>}

          {hata && (
            <p className="mt-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
          )}

          <button
            disabled={mesgul || zayif || uyusmuyor || !eski || !yeni || !tekrar}
            className="mt-5 w-full rounded-(--radius-mod) bg-navy px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {mesgul ? 'Değiştiriliyor…' : 'Şifreyi değiştir'}
          </button>

          <p className="mt-3 text-xs leading-relaxed text-wait">
            Şifre değişince <strong>tüm cihazlardaki oturumlar kapanır</strong> — telefonunuz
            kaybolursa şifrenizi değiştirerek erişimi kesebilirsiniz.
          </p>
        </form>
      )}
    </div>
  );
}


/**
 * E-POSTA — PROFİL BİLGİSİ, GİRİŞ BİLGİSİ DEĞİL.
 *
 * Girişe telefonla devam ediliyor (devir dokümanı: *"saha çalışanı telefonunu
 * bilir, e-postasını bilmeyebilir"*). Adres yalnız iki şeye yarar ve ekranda
 * da açıkça öyle yazıyor — kullanıcı "bunu neden istiyorlar" diye düşünmesin:
 *   1. "Şifremi unuttum" bağlantısının gideceği yer
 *   2. Davet bağlantısı
 *
 * Boş bırakılabilir. O zaman şifreyi yalnız şef/admin sıfırlayabilir.
 */
function EpostaAyari() {
  const { ben, yenile } = useOturum();
  const [deger, setDeger] = useState(ben?.email ?? '');
  const [durum, setDurum] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);

  // Temsil altında başkasının adresi değiştirilemez — o kişinin şifre
  // sıfırlama bağlantısını kendine yönlendirmek demek olurdu.
  if (!ben || ben.temsil) return null;

  const degisti = deger.trim() !== (ben.email ?? '');

  async function kaydet(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setDurum(null);
    setMesgul(true);
    try {
      const c = await api.post<{ ok: boolean; hata?: string }>('/auth/profil', {
        email: deger.trim() || null,
      });
      if (!c.ok) setHata(c.hata ?? 'Kaydedilemedi.');
      else {
        setDurum(deger.trim() ? 'E-posta adresi kaydedildi ✓' : 'E-posta adresi kaldırıldı.');
        await yenile();
      }
    } catch (err) {
      setHata(err instanceof ApiHata ? err.message : 'Kaydedilemedi.');
    } finally {
      setMesgul(false);
    }
  }

  return (
    <form
      onSubmit={kaydet}
      className="mb-5 rounded-(--radius-kart) border border-line bg-surface p-4 shadow-(--shadow-mod)"
    >
      <h2 className="font-semibold text-navy">E-posta adresi</h2>
      <p className="mt-1 mb-3 text-xs leading-relaxed text-wait">
        <strong>Girişte kullanılmaz</strong> — uygulamaya telefon numaranızla girersiniz.
        Adres yalnız <strong>“şifremi unuttum”</strong> bağlantısının gideceği yerdir.
        Boş bırakırsanız şifrenizi yalnız şefiniz sıfırlayabilir.
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="ornek@firma.com"
          value={deger}
          onChange={(e) => setDeger(e.target.value)}
          className="min-w-48 flex-1 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
        />
        <button
          disabled={mesgul || !degisti}
          className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {mesgul ? '…' : 'Kaydet'}
        </button>
      </div>
      {hata && <p className="mt-2 text-xs text-crit">{hata}</p>}
      {durum && <p className="mt-2 text-xs text-ok">{durum}</p>}
    </form>
  );
}

/**
 * BİLDİRİM AYARI — Web Push.
 *
 * ⚠️ İzin isteği KULLANICI HAREKETİNDEN gelmeli. Sayfa açılışında istemek
 *    iOS'ta sessizce reddedilir, Chrome'da kullanıcıyı "engelle"ye iter ve
 *    o karar KALICIDIR — geri almak tarayıcı ayarlarından geçer.
 */
function BildirimAyari() {
  const [durum, setDurum] = useState<PushDurum | null>(null);
  const [mesgul, setMesgul] = useState(false);

  useEffect(() => {
    void pushDurumu().then(setDurum);
  }, []);

  if (durum === null || durum === 'destekyok') return null;

  const kutu = 'mb-5 rounded-(--radius-kart) border p-4 text-sm';

  if (durum === 'kurulumgerek') {
    return (
      <div className={`${kutu} border-warn-line bg-warn-bg text-warn`}>
        <p className="font-semibold">Bildirimler için uygulamayı ana ekrana ekleyin</p>
        <p className="mt-1 text-xs leading-relaxed">
          iPhone/iPad'de bildirim yalnız ana ekrana eklenmiş uygulamada çalışır.
          Safari'de <strong>Paylaş → Ana Ekrana Ekle</strong> deyin, sonra uygulamayı
          oradan açıp buraya dönün.
        </p>
      </div>
    );
  }

  if (durum === 'reddedildi') {
    return (
      <div className={`${kutu} border-line bg-surface-2 text-wait`}>
        <p className="font-semibold text-ink">Bildirimler engellenmiş</p>
        <p className="mt-1 text-xs leading-relaxed">
          Tarayıcı ayarlarından bu siteye bildirim izni vermeniz gerekiyor —
          engelleme kararı buradan geri alınamıyor.
        </p>
      </div>
    );
  }

  const acik = durum === 'acik';

  return (
    <div className={`${kutu} ${acik ? 'border-ok-line bg-ok-bg' : 'border-line bg-surface'}`}>
      <p className={`font-semibold ${acik ? 'text-ok' : 'text-ink'}`}>
        {acik ? 'Bildirimler açık' : 'Bildirimleri aç'}
      </p>
      <p className="mt-1 mb-3 text-xs leading-relaxed text-wait">
        Keşif veya ekip ataması yapıldığında, şefsen onay beklediğinde telefonuna
        bildirim düşer. <strong>Fabrika içinde sinyal olmadığı için bildirim
        kapsama alanına dönünce ulaşır</strong> — İşlerim listesi her koşulda
        doğrudur.
      </p>
      <button
        disabled={mesgul}
        onClick={() => {
          setMesgul(true);
          void (acik ? pushKapat() : pushAc())
            .then(setDurum)
            .finally(() => setMesgul(false));
        }}
        className={`rounded-(--radius-mod) px-4 py-2 text-sm font-medium disabled:opacity-40 ${
          acik ? 'border border-line-2 text-wait' : 'bg-navy text-white'
        }`}
      >
        {mesgul ? '…' : acik ? 'Bildirimleri kapat' : 'Bildirimlere izin ver'}
      </button>
    </div>
  );
}
